import { memo, RefObject, useCallback, useRef, useState } from 'react';

import { ESpeakerInviteStatus, Speaker } from '@/types/speakers.types';
import { updateSpeaker } from '@/store/slices/speakers';
import { revokeGreenroomInvitation, updateHomepageModule, updateMeetingInviteSlice } from '@/store/slices/project';
import { PageModuleType } from '@/types/module.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectGreenroomSessionMeetings,
  selectHomepageAndi18nLanguages,
  selectIsSavingWorkingProject,
  selectSessionByUuid,
  selectWorkingProjectDefaultLanguage,
} from '@/store/slices/project/selectors';
import Popover from '@ui/popover/popover';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import useSpeakersForSession from '@/utils/speakers/use-speakers-for-session';
import useCastAndCrewLayoutType from '../../helpers/use-cast-and-crew-layout-type';
import { getActiveChannel, selectUserChannel } from '@/store/slices/user/selectors';
import useGetSpeakerRole from '../../helpers/use-get-speaker-role';
import handleSendGreenroomInvite from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/cast-and-crew-select-with-invites/components/invite-cast-modal/helpers/handle-send-greenroom-invite';
import syncRemovePresenterFromGreenroomFeeds from './helpers/sync-remove-presenter-from-greenroom-feeds';
/* Commeing out for now - not part of MVP. But all the correct and working logic still exists below */
// import toggleGreenroomWatcherStatus from './helpers/toggle-greenroom-watcher-status';

type Props = {
  speaker: Speaker;
  sessionUuid: string;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  containerRef?: RefObject<HTMLDivElement | null>;
  onEditSpeakerClick: (speaker: Speaker) => void;
  leftAlignPopover?: boolean;
};

const CastActions = memo(
  ({ containerRef, leftAlignPopover, sessionUuid, speaker, onMenuClose, onMenuOpen, onEditSpeakerClick }: Props) => {
    const speakers = useSpeakersForSession({ sessionUuid });

    const portalEl = useRef<HTMLDivElement | null>(null);

    const [sendingInvite, setSendingInvite] = useState(false);

    const dispatch = useAppDispatch();

    const role = useGetSpeakerRole({ speaker, sessionUuid });
    const hasInvite = ['Viewer', 'Presenter'].includes(role);

    const { layout } = useCastAndCrewLayoutType();

    const _defaultLang = useAppSelector(selectWorkingProjectDefaultLanguage);
    const languages = useAppSelector(selectHomepageAndi18nLanguages);
    const session = useAppSelector(selectSessionByUuid(sessionUuid));
    const meetings = useAppSelector(selectGreenroomSessionMeetings);
    const channel = useAppSelector(getActiveChannel);
    const userChannel = useAppSelector(selectUserChannel);
    const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

    const defaultLang = _defaultLang || 'en';

    const sessionMeetingData = meetings?.[sessionUuid || ''] || null;

    // TODO: goes with Hide button
    // const handleToggleHide = useCallback(
    //   (speaker: Speaker) => {
    //     dispatch(
    //       updateSpeaker({
    //         originalSpeaker: speaker,
    //         speaker: {
    //           ...speaker,
    //           hidden: !speaker.hidden,
    //         },
    //         baseLanguage: defaultLang,
    //         languages,
    //       })
    //     );
    //   },
    //   [defaultLang, dispatch, languages]
    // );

    const handleRemoveSpeaker = useCallback(
      async (speakerToRemove: Speaker) => {
        if (!session || !channel) return;

        const speakerModules = session.modules.filter((module) => module.type === PageModuleType.speakers);
        speakerModules.forEach((pageModule) => {
          if (pageModule?.modules && Array.isArray(pageModule.modules)) {
            const updatedModule = {
              ...pageModule,
              modules: pageModule.modules.filter((module: Speaker) => module.speaker !== speakerToRemove.speaker),
              content_modules:
                pageModule.content_modules?.filter((module: number) => module !== speakerToRemove.speaker) ?? [],
            };
            dispatch(
              updateHomepageModule({
                module: updatedModule,
                contentModuleIdsToRemove: [speakerToRemove.speaker],
                moduleIdsToRemove: speakerToRemove.module_id ? [speakerToRemove.module_id] : [],
              })
            );
          }
        });

        onMenuClose?.();

        if (sessionMeetingData.meeting?.meeting && speakerToRemove?.email) {
          await dispatch(
            revokeGreenroomInvitation({
              sessionUuid,
              channel,
              meeting: sessionMeetingData.meeting.meeting,
              email: speakerToRemove.email,
            })
          );

          await syncRemovePresenterFromGreenroomFeeds({ sessionMeetingData, speakerToRemove });
        }
      },
      [session, channel, onMenuClose, sessionMeetingData, dispatch, sessionUuid]
    );

    const handleInviteSpeakerToGreenroom = async () => {
      try {
        if (!userChannel) return;
        setSendingInvite(true);

        const result = await handleSendGreenroomInvite({
          workingSpeaker: speaker,
          setWorkingSpeaker: () => null,
          userChannel,
          meeting: sessionMeetingData?.meeting,
          dispatch,
          setInvitingCast: () => null,
          setGreenroomProfile: () => null,
          setOpenInviteModal: () => null,
          sessionUuid,
        });
        if (result.inviteResponse?.code && sessionUuid) {
          dispatch(
            updateMeetingInviteSlice({
              code: result.inviteResponse.code,
              sessionUuid,
              ...result.inviteResponse,
            })
          );

          // always update invite status if sending invite if we have a speaker.
          const email = result.inviteResponse?.profile?.email || speaker.email;
          const matchedNewAdmin = result.admins?.find((admin) => admin.email === email);
          dispatch(
            updateSpeaker({
              originalSpeaker: speaker,
              baseLanguage: defaultLang,
              languages: languages || [defaultLang],
              speaker: {
                ...speaker,
                invite_status:
                  matchedNewAdmin?.verified || speaker?.bl_admin?.verified
                    ? ESpeakerInviteStatus.confirmed
                    : ESpeakerInviteStatus.pending,
              },
            })
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setSendingInvite(false);
      }
    };

    // Temp commenting out, this is the logic for the commented toggleGreenroomWatcherStatus logic below
    // const speakerBlAdminId = speaker?.bl_admin?.id;
    // const greenroomInvite = sessionMeetingData?.invites?.find((p) => p.profile?.events_profile === speakerBlAdminId);
    // const isWatcher = !!greenroomInvite?.watcher_only;

    if (!session) return null;
    return (
      <Popover
        portalEl={portalEl}
        onOpen={onMenuOpen}
        disabled={savingWorkingProject}
        onClose={onMenuClose}
        width={'fit-content'}
        closeOnTabAway
        recalcutePosition={speakers.length}
        cycleWithArrowKeys
        trackScrollElRef={containerRef}
        trackOnWindowScroll
        renderTriggerButton={({ open }) => (
          <Button
            design="LocalBttnSec"
            height="32px"
            width="32px"
            clear
            borderRadius="100%"
            className="flex-column ai-center jc-center"
            fixedState={open ? 'active' : undefined}
            // stopPropogation because cast cards/rows have click events to open edit speaker details
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={16} />
          </Button>
        )}
      >
        {({ triggerBounds }) => {
          let leftPosition = triggerBounds?.left || 0;
          const triggerWidth = triggerBounds?.width || 1;
          let transform = `translate(-50%, ${(triggerBounds?.height || 32) + 2}px)`;
          if (layout === 'grid') {
            if (leftAlignPopover) {
              transform = `translate(-100%, ${(triggerBounds?.height || 32) + 2}px)`;
              leftPosition += triggerWidth;
            } else {
              leftPosition += triggerWidth / 2;
            }
          } else {
            transform = `translate(-100%, ${(triggerBounds?.height || 32) + 2}px)`;
            leftPosition += triggerWidth;
          }

          return (
            <Container
              className="position-fixed z-20 overflow-y-auto flex-column"
              borderRadius="12px"
              minWidth={'max-content'}
              style={{
                top: triggerBounds?.top || 0,
                left: leftPosition,
                transform,
              }}
              // stopPropogation because cast cards/rows have click events to open edit speaker details
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                design="LocalBttnSec"
                disabled={!speaker.email || hasInvite || sendingInvite}
                borderRadius="0px"
                loading={sendingInvite}
                onClick={handleInviteSpeakerToGreenroom}
              >
                <Text className="P2 flex ai-center gap-8">
                  <Icon name={ICONS.SEND_3} />

                  <span>Invite as presenter</span>
                </Text>
              </Button>

              <Button design="LocalBttnSec" borderRadius="0px" onClick={() => onEditSpeakerClick(speaker)}>
                <Text design="GlobalIcoText1" className="P2 flex ai-center gap-8">
                  <Icon name={ICONS.EDIT} />
                  <span>Edit speaker</span>
                </Text>
              </Button>
              {/*
              TODO: hide for now pending product decision - user can use Remove/Re-add to hide speaker
            */}
              {/* <Button design="LocalBttnSec" borderRadius="0px" onClick={() => handleToggleHide(speaker)}>
                <Text design="GlobalIcoText1" className="P2 flex ai-center gap-8">
                  <Icon name={speaker?.hidden ? ICONS.VIEWERS_EYE_OFF_SOLID : ICONS.VIEWERS_EYE} />
                  <span>Hide from page</span>
                </Text>
              </Button> */}

              {/*
              Temporarily commenting this out because atm product don't want this available.
              For now, this is only allowed to be changed in GR directly.
            */}
              {/* <Activity mode={greenroomInvite ? 'visible' : 'hidden'}>
                <Button
                  design="LocalBttnSec"
                  borderRadius="0px"
                  onClick={() => {
                    toggleGreenroomWatcherStatus({
                      isWatcher: !isWatcher,
                      speakerBlAdminId,
                      session,
                      dispatch,
                      sessionMeetingData,
                    });
                  }}
                >
                  <Text design="GlobalIcoText1" className="P2 flex ai-center gap-8">
                    <Icon name={isWatcher ? ICONS.VIEWERS_EYE_OFF_SOLID : ICONS.VIEWERS_EYE} />
                    <span>Mark as Greenroom® {isWatcher ? 'presenter' : 'viewer'}</span>
                  </Text>
                </Button>
              </Activity> */}

              <Button design="LocalBttnSec" borderRadius="0px" onClick={() => handleRemoveSpeaker(speaker)}>
                <Text color="var(--global-error-500)" className="P2 flex ai-center gap-8">
                  <Icon name={ICONS.EDIT} />
                  <span>Remove</span>
                </Text>
              </Button>
            </Container>
          );
        }}
      </Popover>
    );
  }
);

CastActions.displayName = 'CastActions';

export default CastActions;
